// @ts-nocheck
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { DynamicDropdownPresentationProps } from "@store/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import { ProductPartNumbersStyles } from "@store/Overrides/Components/ProductPartNumbers";
import {
    autocompleteFontStyles,
    autocompleteLinkWithImageStyles,
    autocompleteOptionStyles,
} from "@store/Themes/common/mixins/autocomplete";
import { imageStyles } from "@store/Themes/common/mixins/image";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { inlineSeparatorStyles } from "@store/Themes/common/mixins/typography";
import { inlineFormWithLabelProps } from "@store/Themes/common/props/inlineForm";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
// import { focusOutlineStyles, focusUpdateStyles } from "@toolkit/Themes/common/mixins"; // Store Custom Code
import { css } from "styled-components";

/* Store Custom Code - Start */
import { focusOutlineStyles, focusUpdateStyles, spacingStyles } from "@toolkit/Themes/common/mixins";
import { AvailableAtOtherShipToLabelPresentationProps } from "@store/Library/Components/MultiLocationCatalog/AvailableAtOtherShipToLabel";
import { NotInYourCatalogBadgePresentationProps } from "@store/Library/Components/NotInYourCatalogBadge";
/* Store Custom Code - End */

export interface AutocompleteProps {
    wrapper?: InjectableCss;
    headerText?: TypographyPresentationProps;
    subHeaderText?: TypographyPresentationProps;
    autocompleteColumnWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    focusedLink?: LinkPresentationProps;
    note?: TypographyPresentationProps;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    optionWrapper?: InjectableCss;
    optionWithImageWrapper?: InjectableCss;
    productPartNumbers?: ProductPartNumbersStyles;
    imageWrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    erpNumber?: TypographyPresentationProps;
    // Store Custom Code
    availableAtOtherShipToLabel: AvailableAtOtherShipToLabelPresentationProps;
    notInYourCatalogBadge: NotInYourCatalogBadgePresentationProps;
}

export const autocompleteGetLinkProps = (
    color?: string,
    hoverColor?: string | null,
    underline?: boolean,
    hoverUnderline?: boolean,
): LinkPresentationProps => ({
    css: css`
        ${({ theme }) => {
            return linkStyles(
                color ?? theme.design.autocomplete.link.color,
                hoverColor ?? theme.design.autocomplete.link.hoverColor,
                underline ?? theme.design.autocomplete.link.underline,
                hoverUnderline ?? theme.design.autocomplete.link.hoverUnderline,
            );
        }}

        ${focusUpdateStyles(true)}
        position: relative;
        z-index: 1;
        width: 100%;
        padding: ${pxToRem(5, 0)};
    `,
    typographyProps: {
        ellipsis: true,
        css: css`
            z-index: -1;
            width: 100%;
            text-align: left;
        `,
    },
});

/* Store Custom Code - Start */
const badgeWrapperProps = {
    extendedStyles: {
        wrapper: {
            css: css`
                ${spacingStyles(null, pxToRem(2))}
                float: left; /* prevents the underline hover effect */
            `,
        },
    },
};
/* Store Custom Code - End */

export const autocompleteProps: AutocompleteProps = {
    headerText: {
        css: css`
            display: block;
            margin: ${x => pxToRem(18)} 0 ${x => x.theme.design.autocomplete.columnHeader.margin.horizontal} 0;
            padding-bottom: ${pxToRem(5)};
            text-align: left;
            text-transform: uppercase;
            font-weight: ${x => x.theme.design.typography.weight.bold};
            border-bottom: 1px solid ${getColor("common.border")};

            &:first-child {
                margin-top: 0;
            }
        `,
    },
    subHeaderText: {
        css: css`
            display: block;
            padding: ${pxToRem(5, 0)};
            font-style: italic;
            color: ${getColor("text.accent")};
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            margin-bottom: ${pxToRem(18)};

            &:last-of-type {
                margin-bottom: 0;
            }
        `,
    },
    link: autocompleteGetLinkProps(),
    focusedLink: mergeProps<AutocompleteProps["focusedLink"]>(autocompleteGetLinkProps(), {
        css: css`
            color: ${getColor("primary")};

            &:not(:focus-visible) {
                ${x => focusOutlineStyles(`-${x.theme.focus.width}`)}
            }
        `,
    }),
    note: {
        italic: true,
        css: css`
            font-size: ${x => x.theme.design.typography.size.smallest};
        `,
    },
    productPartNumbers: {
        wrapper: {
            css: css`
                display: flex;
                flex-wrap: wrap;
                margin-top: ${pxToRem(1)};
                font-size: ${x => x.theme.design.typography.size.smallest};
                color: inherit;
                line-height: inherit;
                /* Store Custom Code - Start */
                flex-direction: column;
                font-weight: ${x => x.theme.design.typography.weight.base};
                /* Store Custom Code - End */
            `,
        },
        innerWrapper: {
            css: css`
                /* Store Removed - inlineSeparatorStyles */
                margin-bottom: 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            `,
        },
    },
    imageWrapper: {
        css: css`
            ${x => imageStyles(x.theme.design.product.image.small.ratio)}
            width: ${pxToRem(50)};
            flex: 0 0 auto;
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            width: 0%;
            flex: 1 0 auto;
            flex-direction: column;
        `,
    },
    searchDynamicDropdown: mergeProps<AutocompleteProps["searchDynamicDropdown"]>(inlineFormWithLabelProps.field, {
        cssOverrides: {
            formField: css`
                flex-grow: 999999;
                min-width: 50%;
            `,
            option: autocompleteOptionStyles(),
        },
    }),
    optionWrapper: {
        css: css`
            ${autocompleteFontStyles()}
            display: flex;
            align-items: center;
            cursor: pointer;
        `,
    },
    optionWithImageWrapper: {
        css: css`
            ${autocompleteFontStyles()}
            ${autocompleteLinkWithImageStyles()}
        `,
    },
    title: {
        ellipsis: true,
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    erpNumber: {
        css: css`
            float: left; /* Prevent underline effect */
        `,
    },
    /* Store Custom Code - Start */
    availableAtOtherShipToLabel: badgeWrapperProps,
    notInYourCatalogBadge: badgeWrapperProps,
    /* Store Custom Code - End */
};
